// PillTabs (DESIGN §3.5.2): Pill-Tabs 36 hoch mit Icon und Zähler, gleitender Indikator (motion
// layoutId, 200 ms). `PillTabs` = lokale Tabs (Radix Tabs mit Panels), `PillTabsNav` = Routen-Tabs.
import {
  useEffect,
  useId,
  useRef,
  type ElementType,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { LayoutGroup, motion } from 'motion/react';
import { Tabs as TabsPrimitive } from 'radix-ui';
import { useControllable } from './_lib/useControllable';
import { scrollChildIntoView, useScrollFade } from './_lib/useScrollFade';
import './_lib/motion.css';
import { cn, DURATION, EASE } from '../basis/kern';

export type CountTone = 'default' | 'success' | 'warning' | 'danger';

export interface PillTabItem {
  value: string;
  label: string;
  /** Phosphor-Icon 16 (regular). */
  icon?: ReactNode;
  /** Zähler hinter „ · “, z. B. „78 %“ oder 3. */
  count?: ReactNode;
  countTone?: CountTone;
  disabled?: boolean;
}

/** `page` = auf Seitengrund (aktiv `card`), `card` = Unter-Tabs auf einer Karte (aktiv `tab-sub-active`). */
export type PillTabsSurface = 'page' | 'card';

const toneClass: Record<CountTone, string> = {
  default: 'text-fg-secondary',
  success: 'text-success-fg',
  warning: 'text-warning-fg',
  danger: 'text-danger-fg',
};

export const pillTabClass = (surface: PillTabsSurface, dense: boolean) =>
  cn(
    'relative inline-flex h-9 shrink-0 items-center gap-2 rounded-full px-3.5 whitespace-nowrap select-none',
    'transition-colors duration-(--bar-dur-fast) ease-standard',
    dense ? 'text-button' : 'text-body',
    surface === 'page' ? 'text-fg-nav' : 'text-fg-secondary',
    'hover:bg-pill-page/50 data-[active=true]:text-fg data-[active=true]:hover:bg-transparent',
    'disabled:cursor-not-allowed disabled:text-fg-disabled',
  );

function TabInner({
  item,
  active,
  surface,
  layoutId,
}: {
  item: PillTabItem;
  active: boolean;
  surface: PillTabsSurface;
  layoutId: string;
}) {
  return (
    <>
      {active ? (
        <motion.span
          layoutId={layoutId}
          aria-hidden="true"
          transition={{ duration: DURATION.base / 1000, ease: EASE }}
          className={cn(
            'absolute inset-0 rounded-full',
            surface === 'page' ? 'bg-card' : 'bg-tab-sub-active',
          )}
        />
      ) : null}
      {item.icon ? (
        <span aria-hidden="true" className="relative inline-flex [&>svg]:size-4">
          {item.icon}
        </span>
      ) : null}
      <span className="relative">
        {item.label}
        {item.count !== undefined && item.count !== null ? (
          <>
            {' '}
            <span className={toneClass[item.countTone ?? 'default']}>· {item.count}</span>
          </>
        ) : null}
      </span>
    </>
  );
}

interface CommonProps {
  items: PillTabItem[];
  surface?: PillTabsSurface;
  /** Dichte Bereiche: Label in `button` 15 statt `body` 16. */
  dense?: boolean;
  'aria-label'?: string;
  className?: string;
}

export interface PillTabsProps extends CommonProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Panels als `PillTabsContent`. */
  children?: ReactNode;
}

function useActiveIntoView(listRef: RefObject<HTMLElement | null>, active: string | undefined) {
  useEffect(() => {
    scrollChildIntoView(listRef.current, '[data-active="true"]');
  }, [listRef, active]);
}

export function PillTabs({
  items,
  surface = 'page',
  dense = false,
  value: valueProp,
  defaultValue,
  onValueChange,
  children,
  className,
  ...aria
}: PillTabsProps) {
  const id = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useControllable(
    valueProp,
    defaultValue ?? items[0]?.value ?? '',
    onValueChange,
  );
  useScrollFade(listRef);
  useActiveIntoView(listRef, value);
  return (
    <TabsPrimitive.Root
      value={value}
      onValueChange={setValue}
      className={cn('min-w-0 max-w-full', className)}
    >
      <TabsPrimitive.List
        ref={listRef}
        aria-label={aria['aria-label']}
        className="bar-st-scroll-x bar-st-fade-x flex gap-1 overflow-x-auto"
      >
        <LayoutGroup id={id}>
          {items.map((item) => (
            <TabsPrimitive.Trigger
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              data-active={item.value === value}
              className={pillTabClass(surface, dense)}
            >
              <TabInner
                item={item}
                active={item.value === value}
                surface={surface}
                layoutId="pill"
              />
            </TabsPrimitive.Trigger>
          ))}
        </LayoutGroup>
      </TabsPrimitive.List>
      {children}
    </TabsPrimitive.Root>
  );
}

/** Panel zu einem Tab aus `PillTabs`. */
export function PillTabsContent({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <TabsPrimitive.Content value={value} className={cn('mt-4', className)}>
      {children}
    </TabsPrimitive.Content>
  );
}

export interface PillTabsNavItem extends PillTabItem {
  href: string;
}

export interface PillTabsNavProps extends Omit<CommonProps, 'items'> {
  items: PillTabsNavItem[];
  /** `value` des aktiven Tabs (aus der Route). */
  activeValue?: string;
  /** Link-Komponente, z. B. `Link` aus react-router; Standard `a`. */
  linkAs?: ElementType;
  /** Name der Ziel-Prop der Link-Komponente: `href` (Standard) oder `to` (react-router). */
  hrefProp?: 'href' | 'to';
}

/**
 * Routen-Tabs: jeder Tab ist ein Link, `nav` mit `aria-current="page"`. ←/→, Home/End bewegen den
 * Fokus (Roving), Enter folgt dem Link (DESIGN §3.5.2).
 */
export function PillTabsNav({
  items,
  activeValue,
  surface = 'page',
  dense = false,
  linkAs: Link = 'a',
  hrefProp = 'href',
  className,
  ...aria
}: PillTabsNavProps) {
  const id = useId();
  const listRef = useRef<HTMLDivElement>(null);
  useScrollFade(listRef);
  useActiveIntoView(listRef, activeValue);
  const focusable = items.find((item) => item.value === activeValue) ?? items[0];

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const links = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[data-pill-tab]') ?? [],
    );
    const index = links.indexOf(document.activeElement as HTMLElement);
    if (index < 0) return;
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % links.length
        : event.key === 'ArrowLeft'
          ? (index - 1 + links.length) % links.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? links.length - 1
              : -1;
    if (next < 0) return;
    event.preventDefault();
    links[next]?.focus();
  };

  return (
    <nav aria-label={aria['aria-label']} className={cn('min-w-0 max-w-full', className)}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- Roving-Fokus der Links */}
      <div
        ref={listRef}
        onKeyDown={onKeyDown}
        className="bar-st-scroll-x bar-st-fade-x flex gap-1 overflow-x-auto"
      >
        <LayoutGroup id={id}>
          {items.map((item) => {
            const active = item.value === activeValue;
            return (
              <Link
                key={item.value}
                {...{ [hrefProp]: item.href }}
                data-pill-tab=""
                data-active={active}
                aria-current={active ? 'page' : undefined}
                tabIndex={item === focusable ? 0 : -1}
                className={pillTabClass(surface, dense)}
              >
                <TabInner item={item} active={active} surface={surface} layoutId="pill" />
              </Link>
            );
          })}
        </LayoutGroup>
      </div>
    </nav>
  );
}
